import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Table, Tooltip, Tag, message, Button as AntButton } from 'antd'
import { Input, Switch, Button, Select } from '@/components/ui'
import {
  EditFilled,
  QuestionCircleOutlined,
  HolderOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import EventLayout from '@/components/layouts/EventLayout'
import { TabbedPage } from '@/components/shared/TabbedPage'
import { Icon } from '@/components/icons'
import AddFieldDrawer from './components/AddFieldDrawer'
import type {
  CountryOption,
  Event,
  RegistrationField,
  RegistrationFieldCondition,
  AgendaSession,
} from './types'
import { firstNameLabel, isNameField, isSplitName, lastNameLabel } from './nameField'
import { api } from '@/lib/apiClient'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import TemplateIcon from '@/components/icons/TemplateIcon'

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  listeners?: SyntheticListenerMap
}

const RowContext = React.createContext<RowContextProps>({})

const FIELD_KIND_LABELS: Record<string, string> = {
  short_text: 'Short Text',
  long_text: 'Long Text',
  multiple_choice: 'Multiple Choice',
  single_choice: 'Single Choice',
  dropdown: 'Dropdown',
  file_upload: 'File Upload',
  consent_text: 'Consent Text',
  session_choice: 'Session',
  phone_number: 'Phone Number',
}

interface RegistrationFormProps {
  event: Event
  registration_fields: RegistrationField[]
  agenda_sessions: AgendaSession[]
  registration_field_conditions: RegistrationFieldCondition[]
  country_options: CountryOption[]
}

export default function RegistrationForm({
  event,
  registration_fields,
  agenda_sessions,
  registration_field_conditions,
  country_options,
}: RegistrationFormProps) {
  const [fields, setFields] = useState<RegistrationField[]>(registration_fields)
  const [conditions, setConditions] = useState<RegistrationFieldCondition[]>(
    registration_field_conditions,
  )
  const [savingConditions, setSavingConditions] = useState<Record<string, boolean>>({})
  const [formName, setFormName] = useState(event.registration_form_name)
  const [allowCancellation, setAllowCancellation] = useState(event.allow_cancellation)
  const [allowUpdate, setAllowUpdate] = useState(event.allow_update_registration)
  const [addFieldOpen, setAddFieldOpen] = useState(false)
  const [selectedField, setSelectedField] = useState<RegistrationField | null>(null)

  useEffect(() => {
    setFields(registration_fields)
  }, [registration_fields])

  useEffect(() => {
    setConditions(registration_field_conditions)
  }, [registration_field_conditions])

  const conditionalSourceFields = useMemo(
    () =>
      fields.filter(
        (field) =>
          field.kind === 'multiple_choice' ||
          field.kind === 'single_choice' ||
          field.kind === 'dropdown',
      ),
    [fields],
  )

  const conditionalTargetFields = useMemo(
    () => fields.filter((field) => !field.default_kind),
    [fields],
  )

  const sourceFieldOptions = useMemo(
    () =>
      conditionalSourceFields.map((field) => ({
        value: field.id,
        label: field.label,
      })),
    [conditionalSourceFields],
  )

  const targetFieldOptions = useMemo(
    () =>
      conditionalTargetFields.map((field) => ({
        value: field.id,
        label: field.label,
      })),
    [conditionalTargetFields],
  )

  const createConditionClientId = () =>
    `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

  const getConditionKey = (condition: RegistrationFieldCondition) =>
    condition.id ? `condition-${condition.id}` : condition.client_id || 'new-condition'

  const isConditionComplete = (condition: RegistrationFieldCondition) =>
    Boolean(condition.source_field_id && condition.option_value && condition.target_field_id)

  const persistCondition = async (condition: RegistrationFieldCondition) => {
    if (!isConditionComplete(condition)) return

    const key = getConditionKey(condition)
    if (savingConditions[key]) return

    setSavingConditions((prev) => ({ ...prev, [key]: true }))

    try {
      const payload = {
        condition: {
          source_field_id: condition.source_field_id,
          target_field_id: condition.target_field_id,
          option_value: condition.option_value,
        },
      }

      if (condition.id) {
        const response = await api.patch<{ condition: RegistrationFieldCondition }>(
          `/events/${event.slug}/registration_form/conditions/${condition.id}`,
          payload,
        )
        if (response?.condition) {
          setConditions((prev) =>
            prev.map((existing) =>
              existing.id === condition.id ? { ...existing, ...response.condition } : existing,
            ),
          )
        }
      } else {
        const response = await api.post<{ condition: RegistrationFieldCondition }>(
          `/events/${event.slug}/registration_form/conditions`,
          payload,
        )
        if (response?.condition) {
          setConditions((prev) =>
            prev.map((existing) =>
              existing.client_id === condition.client_id
                ? { ...existing, ...response.condition }
                : existing,
            ),
          )
        }
      }
    } catch (error: any) {
      const apiErrors = error?.data?.errors || error?.errors
      const errorMessage =
        (Array.isArray(apiErrors) && apiErrors.join(', ')) ||
        error?.message ||
        'Failed to save condition'
      message.error(errorMessage)
    } finally {
      setSavingConditions((prev) => ({ ...prev, [key]: false }))
    }
  }

  const handleAddCondition = () => {
    setConditions((prev) => [...prev, { client_id: createConditionClientId() }])
  }

  const handleConditionChange = (index: number, updates: Partial<RegistrationFieldCondition>) => {
    const existing = conditions[index]
    if (!existing) return

    const nextCondition = {
      ...existing,
      client_id: existing.client_id || createConditionClientId(),
      ...updates,
    }
    setConditions((prev) =>
      prev.map((condition, idx) => (idx === index ? nextCondition : condition)),
    )
    persistCondition(nextCondition)
  }

  const handleDeleteCondition = async (condition: RegistrationFieldCondition) => {
    if (!condition.id) {
      if (condition.client_id) {
        setConditions((prev) => prev.filter((item) => item.client_id !== condition.client_id))
      } else {
        setConditions((prev) => prev.filter((item) => item !== condition))
      }
      return
    }

    try {
      await api.delete(`/events/${event.slug}/registration_form/conditions/${condition.id}`)
      setConditions((prev) => prev.filter((item) => item.id !== condition.id))
    } catch (error: any) {
      const apiErrors = error?.data?.errors || error?.errors
      const errorMessage =
        (Array.isArray(apiErrors) && apiErrors.join(', ')) ||
        error?.message ||
        'Failed to delete condition'
      message.error(errorMessage)
    }
  }

  const handleFormNameChange = (value: string) => {
    setFormName(value)
    // TODO: Debounce and save to backend
  }

  const handleCancellationToggle = (checked: boolean) => {
    setAllowCancellation(checked)
    // TODO: Save to backend
  }

  const handleUpdateToggle = (checked: boolean) => {
    setAllowUpdate(checked)
    // TODO: Save to backend
  }

  const handleEdit = (field: RegistrationField) => {
    setSelectedField(field)
    setAddFieldOpen(true)
  }

  const handleFieldSaved = (saved: RegistrationField) => {
    const nextPosition = fields.length > 0 ? Math.max(...fields.map((f) => f.position || 0)) + 1 : 0
    const idx = fields.findIndex((f) => f.id === saved.id)

    if (idx >= 0) {
      const clone = [...fields]
      clone[idx] = { ...saved, position: saved.position ?? clone[idx].position }
      setFields(clone)
    } else {
      const position = saved.position && saved.position > 0 ? saved.position : nextPosition
      setFields([...fields, { ...saved, position }])
    }

    setSelectedField(null)
  }

  // Drag & Drop (AntD documented pattern)
  const DragHandle: React.FC = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext)
    return (
      <AntButton
        type="text"
        size="small"
        icon={<HolderOutlined />}
        style={{ cursor: 'move' }}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    )
  }

  interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string
  }

  const Row: React.FC<RowProps> = (props) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: props['data-row-key'] })

    const style: React.CSSProperties = {
      ...props.style,
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging
        ? {
            position: 'relative',
            zIndex: 9999,
            userSelect: 'none',
            boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
          }
        : {}),
    }

    const contextValue = useMemo<RowContextProps>(
      () => ({ setActivatorNodeRef, listeners }),
      [setActivatorNodeRef, listeners],
    )

    return (
      <RowContext.Provider value={contextValue}>
        <tr {...props} ref={setNodeRef} style={style} {...attributes} />
      </RowContext.Provider>
    )
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
  )

  const persistReorder = async (
    fieldId: number,
    newIndex: number,
    fallbackFields: RegistrationField[],
  ) => {
    try {
      const response = await api.patch<{ fields: RegistrationField[] }>(
        `/events/${event.slug}/registration_form/fields/${fieldId}/reorder`,
        { position: newIndex },
      )
      if (response?.fields) {
        setFields(response.fields)
      }
    } catch (error: any) {
      setFields(fallbackFields)
      const apiErrors = error?.data?.errors || error?.errors
      const errorMessage =
        (Array.isArray(apiErrors) && apiErrors.join(', ')) ||
        error?.message ||
        'Failed to reorder field'
      message.error(errorMessage)
    }
  }

  const updateFieldConfiguration = async (
    fieldId: number,
    configUpdates: Record<string, unknown>,
  ) => {
    const previousFields = fields
    setFields((prev) =>
      prev.map((field) =>
        field.id === fieldId
          ? { ...field, configuration: { ...field.configuration, ...configUpdates } }
          : field,
      ),
    )

    try {
      const response = await api.patch<{
        success: boolean
        field?: RegistrationField
        errors?: string[]
      }>(`/events/${event.slug}/registration_form/fields/${fieldId}`, {
        field: { configuration: configUpdates },
      })

      if (response?.field) {
        setFields((prev) => prev.map((field) => (field.id === fieldId ? response.field! : field)))
      }
    } catch (error: any) {
      setFields(previousFields)
      const apiErrors = error?.data?.errors || error?.errors
      const errorMessage =
        (Array.isArray(apiErrors) && apiErrors.join(', ')) ||
        error?.message ||
        'Failed to update field'
      // These are the server's own validation messages, and some are an
      // instruction rather than a notice — refusing the split-name toggle names
      // the field that has to go first. Three seconds is not long enough to read.
      message.error(errorMessage, errorMessage.length > 80 ? 8 : undefined)
    }
  }

  const updateField = async (
    fieldId: number,
    updates: Partial<Pick<RegistrationField, 'required' | 'shown_on_form' | 'editable'>>,
  ) => {
    const previousFields = fields
    const payload: Record<string, unknown> = { ...updates }

    if (Object.prototype.hasOwnProperty.call(payload, 'shown_on_form')) {
      payload.show_on_form = payload.shown_on_form
      delete payload.shown_on_form
    }

    setFields((prev) =>
      prev.map((field) => (field.id === fieldId ? { ...field, ...updates } : field)),
    )

    try {
      const response = await api.patch<{
        success: boolean
        field?: RegistrationField
        errors?: string[]
      }>(`/events/${event.slug}/registration_form/fields/${fieldId}`, {
        field: payload,
      })

      if (response?.field) {
        setFields((prev) => prev.map((field) => (field.id === fieldId ? response.field! : field)))
      }
    } catch (error: any) {
      setFields(previousFields)
      const apiErrors = error?.data?.errors || error?.errors
      const errorMessage =
        (Array.isArray(apiErrors) && apiErrors.join(', ')) ||
        error?.message ||
        'Failed to update field'
      message.error(errorMessage)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeIndex = fields.findIndex((f) => f.id.toString() === active.id.toString())
    const overIndex = fields.findIndex((f) => f.id.toString() === over.id.toString())
    if (activeIndex === -1 || overIndex === -1) return

    const previous = fields
    const reordered = arrayMove(fields, activeIndex, overIndex).map((f, idx) => ({
      ...f,
      position: idx,
    }))
    setFields(reordered)
    persistReorder(Number(active.id), overIndex, previous)
  }

  // Columns for the fields table
  const columns: ColumnsType<RegistrationField> = [
    {
      title: '',
      key: 'drag',
      width: 56,
      align: 'center',
      render: () => <DragHandle />,
    },
    {
      title: 'Field',
      dataIndex: 'label',
      key: 'field',
      render: (label, record) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-900">{label}</span>
            {record.is_default && (
              <Tag color="blue" className="text-xs">
                Default
              </Tag>
            )}
            <Tag className="text-xs">{FIELD_KIND_LABELS[record.kind] || record.kind}</Tag>
          </div>
          {isSplitName(record) && (
            <p className="text-xs text-gray-500 mt-1">
              Collected as {firstNameLabel(record)} + {lastNameLabel(record)}
            </p>
          )}
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center justify-center gap-1">
          Editable
          <Tooltip title="Allow registrants to update this field later in the attendee app">
            <QuestionCircleOutlined className="text-gray-400 text-xs" />
          </Tooltip>
        </div>
      ),
      dataIndex: 'editable',
      key: 'editable',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={record.editable}
          size="small"
          onChange={(checked) => updateField(record.id, { editable: checked })}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-center gap-1">
          Required
          <Tooltip title="Mark as a required field">
            <QuestionCircleOutlined className="text-gray-400 text-xs" />
          </Tooltip>
        </div>
      ),
      dataIndex: 'required',
      key: 'required',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={record.required}
          size="small"
          onChange={(checked) => updateField(record.id, { required: checked })}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-center gap-1">
          Show on Form
          <Tooltip title="Display this field on the registration form">
            <QuestionCircleOutlined className="text-gray-400 text-xs" />
          </Tooltip>
        </div>
      ),
      dataIndex: 'shown_on_form',
      key: 'shown_on_form',
      width: 160,
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={record.shown_on_form}
          size="small"
          disabled={record.identifier === 'email'}
          onChange={(checked) => updateField(record.id, { shown_on_form: checked })}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-center gap-1">
          Confirm
          <Tooltip title="Require registrants to enter this field twice to confirm accuracy">
            <QuestionCircleOutlined className="text-gray-400 text-xs" />
          </Tooltip>
        </div>
      ),
      key: 'confirm',
      width: 140,
      align: 'center',
      render: (_, record) =>
        record.default_kind === 'email' || record.default_kind === 'phone' ? (
          <Switch
            checked={record.configuration?.require_confirmation || false}
            size="small"
            onChange={(checked) =>
              updateFieldConfiguration(record.id, { require_confirmation: checked })
            }
          />
        ) : null,
    },
    {
      title: (
        <div className="flex items-center justify-center gap-1">
          Split Name
          <Tooltip title="Collect first and last name as two separate inputs instead of one full name">
            <QuestionCircleOutlined className="text-gray-400 text-xs" />
          </Tooltip>
        </div>
      ),
      key: 'split_name',
      width: 140,
      align: 'center',
      render: (_, record) =>
        isNameField(record) ? (
          <Switch
            checked={isSplitName(record)}
            size="small"
            onChange={(checked) =>
              updateFieldConfiguration(record.id, {
                name_format: checked ? 'split' : 'combined',
              })
            }
          />
        ) : null,
    },
    {
      title: 'Edit Display Name',
      key: 'actions',
      width: 180,
      align: 'center',
      render: (_, record) => (
        <Button
          variant="tertiary"
          icon={<EditFilled />}
          size="small"
          onClick={() => handleEdit(record)}
        >
          Edit
        </Button>
      ),
    },
  ]

  // Sort fields deterministically by saved position for drag order
  const sortedFields = [...fields].sort((a, b) => a.position - b.position)
  const canAddCondition = conditionalSourceFields.length > 0 && conditionalTargetFields.length > 0

  const fieldSettingTab = (
    <div className="ava-settings-table">
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedFields.map((f) => f.id.toString())}
          strategy={verticalListSortingStrategy}
        >
          <Table
            columns={columns}
            dataSource={sortedFields}
            rowKey={(row) => row.id.toString()}
            pagination={false}
            size="middle"
            showSorterTooltip={false}
            components={{
              body: { row: Row },
            }}
          />
        </SortableContext>
      </DndContext>
    </div>
  )

  const conditionalLogicTab = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Conditional Logic</h3>
          <p className="text-sm text-gray-500">
            Show fields based on answers to single choice, multiple choice, or dropdown questions.
          </p>
        </div>
        <Button
          variant="primary"
          size="small"
          onClick={handleAddCondition}
          disabled={!canAddCondition}
        >
          Add Condition
        </Button>
      </div>

      {conditions.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center text-gray-500">
          <p className="text-sm">No conditions yet.</p>
          <p className="text-xs mt-1">
            Add a single choice, multiple choice, or dropdown field and a custom field to get
            started.
          </p>
          <div className="mt-4">
            <Button
              variant="secondary"
              size="small"
              onClick={handleAddCondition}
              disabled={!canAddCondition}
            >
              Add Condition
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {conditions.map((condition, index) => {
            const sourceField = fields.find((field) => field.id === condition.source_field_id)
            const sourceOptions = sourceField?.options || []
            const conditionKey = getConditionKey(condition)

            return (
              <div key={conditionKey} className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700">If</span>
                      <Select
                        placeholder="Select a field"
                        options={sourceFieldOptions}
                        value={condition.source_field_id}
                        onChange={(value) =>
                          handleConditionChange(index, {
                            source_field_id: value as number,
                            option_value: undefined,
                          })
                        }
                        size="small"
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '')
                            .toString()
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{ minWidth: 240 }}
                      />
                    </div>

                    {condition.source_field_id && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Is Equal to</span>
                        <Select
                          placeholder={
                            sourceOptions.length ? 'Select an option' : 'No options available'
                          }
                          options={sourceOptions.map((option) => ({
                            value: option.value,
                            label: option.label,
                          }))}
                          value={condition.option_value}
                          onChange={(value) =>
                            handleConditionChange(index, { option_value: value as string })
                          }
                          size="small"
                          disabled={sourceOptions.length === 0}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '')
                              .toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          style={{ minWidth: 240 }}
                        />
                      </div>
                    )}

                    {condition.source_field_id && condition.option_value && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Then Show</span>
                        <Select
                          placeholder="Select a field"
                          options={targetFieldOptions}
                          value={condition.target_field_id}
                          onChange={(value) =>
                            handleConditionChange(index, { target_field_id: value as number })
                          }
                          size="small"
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '')
                              .toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          style={{ minWidth: 240 }}
                        />
                      </div>
                    )}

                    {savingConditions[conditionKey] && (
                      <div className="text-xs text-gray-400">Saving...</div>
                    )}
                  </div>

                  <AntButton
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteCondition(condition)}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <EventLayout>
      <TabbedPage
        title="Registration Form"
        subtitle="Configure registration fields for attendees"
        headerAction={
          <div className="flex gap-3">
            <Button
              variant="secondary"
              icon={<TemplateIcon />}
              onClick={() => message.info('Template library coming soon!')}
            >
              Templates
            </Button>
            <Button
              variant="primary"
              icon={<EditFilled />}
              onClick={() => {
                setSelectedField(null)
                setAddFieldOpen(true)
              }}
            >
              Add Form Field
            </Button>
          </div>
        }
        tabs={[
          {
            key: 'field_setting',
            label: 'Field Setting',
            children: fieldSettingTab,
          },
          {
            key: 'conditional_logic',
            label: 'Conditional Logic',
            children: conditionalLogicTab,
          },
        ]}
        tabSize="small"
      >
        {/* Form Settings Section */}
        {/* <div className="px-6 py-6 w-full">
          <div className="space-y-6">
            <div style={{ maxWidth: '448px' }}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Registration Form Name
              </label>
              <Input
                value={formName}
                onChange={(e) => handleFormNameChange(e.target.value)}
                placeholder="Enter Form Name"
                maxLength={50}
              />
              <div className="text-xs text-gray-400 mt-1">{formName.length}/50 characters</div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <Switch checked={allowCancellation} onChange={handleCancellationToggle} />
                <span className="text-sm text-gray-700 ml-3">
                  Allow Attendees to Cancel Registration
                </span>
              </div>

              <div>
                <div className="flex items-center">
                  <Switch checked={allowUpdate} onChange={handleUpdateToggle} />
                  <span className="text-sm text-gray-700 ml-3">
                    Allow Attendees to Update Registration
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-12">
                  When enabled, attendees can edit fields you mark as Editable in the table above.
                </p>
              </div>
            </div>
          </div>
        </div> */}
      </TabbedPage>

      {/* Drawers */}
      <AddFieldDrawer
        open={addFieldOpen}
        onClose={() => {
          setAddFieldOpen(false)
          setSelectedField(null)
        }}
        field={selectedField}
        agendaSessions={agenda_sessions}
        countryOptions={country_options}
        eventSlug={event.slug}
        nextPosition={fields.length > 0 ? Math.max(...fields.map((f) => f.position || 0)) + 1 : 0}
        onSaved={handleFieldSaved}
        onDeleted={(id) => {
          setFields((prev) => prev.filter((f) => f.id !== id))
          setConditions((prev) =>
            prev.filter(
              (condition) => condition.source_field_id !== id && condition.target_field_id !== id,
            ),
          )
        }}
      />
    </EventLayout>
  )
}
